import { IConversationList, IConversationTitle, IErrorDetail } from "Common/Interfaces";
import { useEffect, useState } from "react";
import {
  useDeleteConversationMutation,
  useGetConversationHistoryQuery,
  useRenameConversationMutation,
} from "Services/Conversations";

import { CONTEXT_MENU_EVENTS, DIALOG_EVENT, SNACKBAR_SEVERITY, UI_ELEMENTS } from "Common/Constants";
import { useNavigate, useParams } from "react-router-dom";
import { CONVERSATION_MESSAGES, DEFAULT_CONVERSATION_PAGE_SIZE } from "./../Constants";
import { applyConversationTitle, groupConversationsByDateRange, validateConversationTitle } from "./../Functions";
import { useSelector } from "react-redux";
import { RootState } from "src/Store";
import { ISideBar } from "../Sidebar";

export const useSidebar = ({ uiElementState, handleCloseUIElement, setUIElementState }: ISideBar) => {
  const [conversationList, setConversationList] = useState<IConversationList>();
  const [editChatTitleId, setEditChatTitleId] = useState<string | undefined>();

  const conversationLoading = useSelector((state: RootState) => state.conversation.conversationLoading);

  const { data, isLoading: isConversationsLoading } = useGetConversationHistoryQuery({
    pageSize: DEFAULT_CONVERSATION_PAGE_SIZE,
    continuationToken: undefined,
  });

  const [renameConversation] = useRenameConversationMutation();
  const [deleteConversation, { isLoading: isDeleteLoading }] = useDeleteConversationMutation();

  const { chatId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    setConversationList(data);
  }, [data]);

  useEffect(() => {
    setEditChatTitleId(undefined);
  }, [chatId]);

  const handleOpenSnackbar = (message: string, isSuccess: boolean, isWarning?: boolean) => {
    setUIElementState((prevState) => ({
      ...prevState,
      anchorEl: null,
      activeElement: UI_ELEMENTS.SNACKBAR,
      snackbarMessage: message,
      snackbarSeverity: isSuccess
        ? SNACKBAR_SEVERITY.SUCCESS
        : isWarning
          ? SNACKBAR_SEVERITY.WARNING
          : SNACKBAR_SEVERITY.ERROR,
    }));
  };

  const handleDeleteChat = () => {
    const conversationId = uiElementState?.dataRow?.conversationId ?? "";
    if (conversationId && conversationId === conversationList?.conversations[0].conversationId && conversationLoading) {
      handleOpenSnackbar(CONVERSATION_MESSAGES.DELETE_BLOCKED_WHILE_LOADING, false, true);
      return;
    }

    if (conversationId) {
      deleteConversation(conversationId)
        .unwrap()
        .then(() => {
          if (conversationId === chatId || conversationId === conversationList?.conversations[0].conversationId) {
            navigateToStart();
          }
          handleOpenSnackbar(`${uiElementState?.dataRow?.title} deleted successfully.`, true);
        })
        .catch((err: IErrorDetail) => {
          handleOpenSnackbar(err.message, false);
        });
    }
  };

  const handleTitleRename = async (conversationId: string, newTitle: string) => {
    const validationError = validateConversationTitle(newTitle);
    if (validationError) {
      handleOpenSnackbar(validationError, false);
      return;
    }

    // Optimistically show the new title before the request resolves
    const updatedConversationList = applyConversationTitle(conversationList, conversationId, newTitle);
    if (updatedConversationList) {
      setConversationList(updatedConversationList);
    }

    await renameConversation({ id: conversationId, title: newTitle })
      .unwrap()
      .then(() => {
        handleOpenSnackbar(CONVERSATION_MESSAGES.RENAME_SUCCESS, true);
      })
      .catch((err: IErrorDetail) => {
        handleOpenSnackbar(err.message, false);
      });
    setEditChatTitleId(undefined);
  };

  const handleContextMenuButtonClick = (event: React.MouseEvent<HTMLButtonElement>, row: IConversationTitle) => {
    setUIElementState((prevState) => ({
      ...prevState,
      anchorEl: event.currentTarget,
      activeElement: UI_ELEMENTS.CONTEXT_MENU,
      dataRow: row,
    }));
  };

  const handleContextMenuItemClick = (event: CONTEXT_MENU_EVENTS) => {
    switch (event) {
      case CONTEXT_MENU_EVENTS.EDIT:
        setEditChatTitleId(uiElementState?.dataRow?.conversationId);
        handleCloseUIElement?.();
        break;

      case CONTEXT_MENU_EVENTS.DELETE:
        setUIElementState((prevState) => ({
          ...prevState,
          anchorEl: null,
          activeElement: UI_ELEMENTS.DELETE_CONFIRMATION,
          dataRow: uiElementState?.dataRow ?? prevState.dataRow, // Fallback to previous dataRow
        }));
        break;

      default:
        break;
    }
  };

  const handleConfirmationDialogEvent = (event: DIALOG_EVENT) => {
    switch (event) {
      case DIALOG_EVENT.CONFIRM:
        if (uiElementState?.activeElement === UI_ELEMENTS.DELETE_CONFIRMATION) {
          handleDeleteChat();
        }
        if (uiElementState?.activeElement === UI_ELEMENTS.CANCEL_CONFIRMATION) {
          handleCloseUIElement?.();
        }
        break;
      case DIALOG_EVENT.CANCEL:
        handleCloseUIElement?.();
        break;
      default:
        break;
    }
  };

  const navigateToStart = () => {
    navigate("/", { state: { id: Math.random() } });
  };

  const handleNavigateToChat = (id: string | undefined) => {
    if (!conversationLoading && id) {
      navigate(`/c/${id}`);
    } else {
      handleOpenSnackbar(CONVERSATION_MESSAGES.CONVERSATION_LOADING, false, true);
    }
  };

  return {
    handleConfirmationDialogEvent,
    groupConversationsByDateRange,
    renameConversation,
    deleteConversation,
    conversationList,
    isConversationsLoading,
    isDeleteLoading,
    handleContextMenuButtonClick,
    uiElementState,
    handleCloseUIElement,
    handleContextMenuItemClick,
    handleTitleRename,
    editChatTitleId,
    setEditChatTitleId,
    navigateToStart,
    chatId,
    conversationLoading,
    handleNavigateToChat,
    handleOpenSnackbar,
  };
};
